/**
 * RTK Query base query for portal2.
 * Responsibilities (all things portal's axiosInterceptors.js did):
 * - Attach Authorization: Bearer from TokenStore
 * - On 401, single-flight refresh, then retry the original request
 * - On refresh failure, clear all tokens and emit a logout signal
 * The login endpoint is exempted from refresh handling (matches portal behavior).
 */
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { tokenStore } from '@lib/tokenStore';
import { services } from '@config/services';
import { logger } from '@lib/logger';
import { HttpError } from '@lib/httpError';

export interface PortalBaseQueryExtraOptions {
  /** Skip the 401 -> refresh -> retry flow (e.g. login itself). */
  skipAuthRetry?: boolean;
}

/**
 * Hook called when refresh fails terminally. Wired in main.tsx to dispatch
 * a logout action + redirect. Kept as a setter to avoid coupling the base
 * query to the router or store.
 */
let onAuthFailure: () => void = () => {
  window.location.assign('/signin');
};
export function setOnAuthFailure(handler: () => void): void {
  onAuthFailure = handler;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: (headers) => {
    const accessToken = tokenStore.getAccessToken();
    if (accessToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});

/**
 * The effective HTTP status of a failed query. fetchBaseQuery reports a numeric
 * `status` for normal HTTP errors, but when the response body can't be parsed
 * as the expected type — e.g. a gateway/ingress returns a 403 with an HTML or
 * empty body instead of JSON — it reports `status: 'PARSING_ERROR'` and stashes
 * the real code in `originalStatus`. We must read both, or an expired-token 403
 * from the RBAC gateway slips past the refresh path and strands the session
 * (no refresh, no retry, no sign-out — every request just 403s). Legacy axios
 * never hit this: it surfaced the numeric status regardless of the body.
 */
export function httpStatusOf(error: FetchBaseQueryError | undefined): number | undefined {
  if (!error) return undefined;
  if (typeof error.status === 'number') return error.status;
  if ('originalStatus' in error && typeof error.originalStatus === 'number') {
    return error.originalStatus;
  }
  return undefined;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Shape returned by `POST /v1/refresh`. Tolerates both the flat and
 * `{tokenResponse: {...}}`-nested variants the user-service has shipped
 * at various points. `refreshToken` is intentionally optional — the
 * backend does not always rotate refresh tokens on every refresh; when
 * the response omits it, the existing localStorage value stays valid
 * and a future refresh reuses it. (Matches the legacy
 * `portal/axiosInterceptors.js#refreshToken` which only checked
 * `!response.data?.isError` and never required a new refreshToken.)
 *
 * This is *intentionally separate* from `extractTokens` /
 * `normalizeLoginResponse` used by login + exchangeCode, where a
 * refreshToken in the response IS required — a brand-new session has
 * no prior refresh token to fall back to.
 */
interface RefreshResponse {
  tokenResponse?: {
    accessToken?: string;
    refreshToken?: string;
    expiresIn?: number;
  };
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  isError?: boolean;
}

/**
 * Public refresh helper. Used by SSE middleware AND by the in-flight
 * 401-retry path below. Lenient on `refreshToken` — see RefreshResponse
 * docstring for why.
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${services.user}/v1/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as RefreshResponse;
      if (data.isError) return false;
      const nested = data.tokenResponse;
      const accessToken = nested?.accessToken ?? data.accessToken;
      const expiresIn = nested?.expiresIn ?? data.expiresIn;
      const newRefreshToken = nested?.refreshToken ?? data.refreshToken;
      // Only access + expiresIn are required. Match legacy + AuthBootstrap.
      if (!accessToken || typeof expiresIn !== 'number') return false;
      tokenStore.setAccessToken(accessToken, expiresIn);
      // Rotate only when the server gave us a new one.
      if (newRefreshToken) tokenStore.setRefreshToken(newRefreshToken);
      return true;
    } catch (err) {
      logger.error(err, { context: 'refreshAccessToken' });
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Terminal sign-out for a session that can no longer be sustained (the
 * refresh token is missing, expired, or rejected). Clears tokens, signals
 * the app (userSignedOut + navigate to /signin via onAuthFailure), then
 * hangs for a few seconds so the navigation completes before the caller's
 * `.unwrap().catch(...)` (or RTK Query hook's `error` field) gets a chance
 * to surface a toast that the user would see for one frame, then watch
 * disappear as the page tears down. Mirrors the `setTimeout(...,5000)`
 * rejection the legacy axios response interceptor used for the same purpose.
 */
async function endUnrecoverableSession(): Promise<void> {
  tokenStore.clearAll();
  onAuthFailure();
  await new Promise((resolve) => {
    setTimeout(resolve, 5_000);
  });
}

/**
 * Reactive refresh + single retry for direct fetch/XHR sends that bypass
 * portalBaseQuery (multipart uploads). `send` must read its bearer on every
 * call (via getValidToken) and reject with an HttpError on a non-2xx
 * response. Same rule as the RTK path below: a 401/403 is retried once on a
 * freshly minted token, so a 403 that reaches the caller is a real denial;
 * when the refresh fails the session is ended and the send rejects with a
 * 401 "Session expired" rather than the raw gateway response.
 */
export async function sendWithAuthRetry<T>(send: () => Promise<T>): Promise<T> {
  try {
    return await send();
  } catch (error) {
    if (!(error instanceof HttpError) || (error.status !== 401 && error.status !== 403)) throw error;
    if (await refreshAccessToken()) return send();
    await endUnrecoverableSession();
    throw new HttpError('Session expired', 401);
  }
}

/**
 * The exported base query. Handles 401/403 -> refresh -> retry
 * (403 because the RBAC gateway returns it for expired tokens too, sometimes
 * with a non-JSON body that surfaces as PARSING_ERROR — see httpStatusOf).
 */
export const portalBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  PortalBaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const url = typeof args === 'string' ? args : args.url;

  // Proactive refresh: if an access token EXISTS but has expired — i.e. a
  // signed-in session has gone stale — refresh BEFORE firing the request.
  // Without this, every expired-but-not-yet-revoked-by-the-server token
  // sends one doomed request that the backend may or may not bother
  // returning 401 for (some endpoints return 200 with an `{isError: true}`
  // body instead, which the reactive path below can't detect). The
  // `refreshInFlight` singleton dedupes parallel callers.
  //
  // The gate is the ACCESS token, not the refresh token: the refresh token
  // can be gone while a stale session lingers (another tab's sign-out
  // clears the shared localStorage entry but not this tab's sessionStorage
  // access token; private-mode localStorage writes fail silently). In that
  // state refreshAccessToken() returns false without a network call and
  // the session ends below — the legacy interceptor attempted the refresh
  // with an undefined token and signed out on its failure, same outcome.
  // Truly unauthenticated calls (no access token at all) skip the branch.
  //
  // Login + skip-retry endpoints opt out — they're allowed to send an
  // expired or missing token (they're establishing a NEW session, or
  // explicitly handle their own auth errors).
  const isLoginRequest = url.includes('/login');
  const skipAuth = !!extraOptions?.skipAuthRetry;
  if (
    !isLoginRequest &&
    !skipAuth &&
    tokenStore.getAccessToken() !== null &&
    tokenStore.isAccessTokenExpired()
  ) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      // The session is over: the access token has expired AND the refresh
      // token can't mint a new one. Don't fire the doomed request — in
      // deployed (cross-origin) environments the gateway's 401 for a dead
      // bearer carries no CORS headers, so the browser masks it as
      // FETCH_ERROR and the reactive 401/403 branch below never sees it,
      // leaving the user "signed in" on pages that all fail to load. The
      // legacy axios REQUEST interceptor signed out on this exact condition;
      // relying on the response status alone silently dropped that behavior.
      await endUnrecoverableSession();
      return { error: { status: 401, data: { message: 'Session expired' } } };
    }
  }

  let result = await rawBaseQuery(args, api, extraOptions ?? {});

  // Recover from an expired/invalid session. 401 is the canonical signal, but
  // OrderMesh's RBAC gateway also returns 403 for an expired (or otherwise
  // rejected) access token — not only for genuine permission denials. The
  // client's cached token expiry can lag the server's (clock skew, or an
  // `expiresIn` longer than the token's real TTL), so the proactive refresh
  // above may not have fired and we can't tell a "stale session" 403 from a
  // "forbidden" 403 up front. So on either status we attempt a single
  // refresh+retry to self-correct:
  // - refresh succeeds → retry once. A 403 that SURVIVES a brand-new token is
  // a real RBAC denial; it's returned untouched (we do NOT sign out — the
  // session is provably valid, we just minted a token).
  // - refresh fails → the session can't be sustained (no / expired refresh
  // token). Clear tokens + the persisted user (via onAuthFailure →
  // userSignedOut) and redirect to /signin. Together with the proactive
  // branch above, this prevents a "zombie" state: currentUser is persisted in
  // sessionStorage now, so without signing out the user looks logged in while
  // every request 401/403s.
  const status = httpStatusOf(result.error);
  if (
    (status === 401 || status === 403) &&
    !isLoginRequest &&
    !extraOptions?.skipAuthRetry
  ) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      result = await rawBaseQuery(args, api, extraOptions ?? {});
    } else {
      await endUnrecoverableSession();
    }
  }

  return result;
};
